import type { ResumeData } from "@/types/resume";
import { Mail, Phone, MapPin, Globe, Linkedin, Github } from "lucide-react";
import { formatDate, getOrderedSections } from "./templateUtils";
import { getFontPair } from "./useResumeFonts";

export type Layout =
  | "single"          // single column, centered header
  | "single-left"     // single column, left-aligned header
  | "sidebar-left"    // two-column, sidebar on left
  | "sidebar-right"   // two-column, sidebar on right
  | "band"            // colored band header, single column
  | "split-header";   // top bar with name + side info, single column

export interface TemplateTheme {
  layout: Layout;
  font: string;            // body font stack
  headingFont?: string;
  text: string;            // body text color
  muted: string;           // muted text color (must meet a11y; default #475569)
  primary: string;         // primary accent
  primaryText?: string;    // text color over primary backgrounds
  sidebarBg?: string;
  sidebarText?: string;
  bandBg?: string;
  bandText?: string;
  accentRule?: string;     // border color for section dividers
  uppercaseHeadings?: boolean;
  headingTrack?: string;   // letter-spacing
  bullet?: "disc" | "dash" | "square" | "none";
  showIcons?: boolean;
}

const ICON_W = "w-2.5 h-2.5";

function ContactBlock({ p, color = "#475569", iconColor, showIcons = true }: { p: ResumeData["personalInfo"]; color?: string; iconColor?: string; showIcons?: boolean }) {
  const ic = iconColor || color;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]" style={{ color }}>
      {p.email && <span className="flex items-center gap-1">{showIcons && <Mail className={ICON_W} style={{ color: ic }} />}{p.email}</span>}
      {p.phone && <span className="flex items-center gap-1">{showIcons && <Phone className={ICON_W} style={{ color: ic }} />}{p.phone}</span>}
      {p.city && <span className="flex items-center gap-1">{showIcons && <MapPin className={ICON_W} style={{ color: ic }} />}{p.city}{p.country ? `, ${p.country}` : ""}</span>}
      {p.linkedin && <span className="flex items-center gap-1">{showIcons && <Linkedin className={ICON_W} style={{ color: ic }} />}<span className="break-all">{p.linkedin}</span></span>}
      {p.github && <span className="flex items-center gap-1">{showIcons && <Github className={ICON_W} style={{ color: ic }} />}<span className="break-all">{p.github}</span></span>}
      {p.portfolio && <span className="flex items-center gap-1">{showIcons && <Globe className={ICON_W} style={{ color: ic }} />}<span className="break-all">{p.portfolio}</span></span>}
    </div>
  );
}

function ContactStack({ p, color = "#475569", iconColor, showIcons = true }: { p: ResumeData["personalInfo"]; color?: string; iconColor?: string; showIcons?: boolean }) {
  const ic = iconColor || color;
  return (
    <div className="space-y-1 text-[10px]" style={{ color }}>
      {p.email && <div className="flex items-start gap-1">{showIcons && <Mail className={`${ICON_W} mt-[2px] shrink-0`} style={{ color: ic }} />}<span className="break-all">{p.email}</span></div>}
      {p.phone && <div className="flex items-start gap-1">{showIcons && <Phone className={`${ICON_W} mt-[2px] shrink-0`} style={{ color: ic }} />}<span>{p.phone}</span></div>}
      {p.city && <div className="flex items-start gap-1">{showIcons && <MapPin className={`${ICON_W} mt-[2px] shrink-0`} style={{ color: ic }} />}<span>{p.city}{p.country ? `, ${p.country}` : ""}</span></div>}
      {p.linkedin && <div className="flex items-start gap-1">{showIcons && <Linkedin className={`${ICON_W} mt-[2px] shrink-0`} style={{ color: ic }} />}<span className="break-all">{p.linkedin}</span></div>}
      {p.github && <div className="flex items-start gap-1">{showIcons && <Github className={`${ICON_W} mt-[2px] shrink-0`} style={{ color: ic }} />}<span className="break-all">{p.github}</span></div>}
      {p.portfolio && <div className="flex items-start gap-1">{showIcons && <Globe className={`${ICON_W} mt-[2px] shrink-0`} style={{ color: ic }} />}<span className="break-all">{p.portfolio}</span></div>}
    </div>
  );
}

function bulletList(items: string[], style: TemplateTheme["bullet"] = "disc") {
  if (!items.length) return null;
  if (style === "dash") return <ul className="list-none mt-1 space-y-0.5">{items.map((r, i) => <li key={i} className="pl-3 relative before:content-['—'] before:absolute before:left-0 before:text-gray-400">{r}</li>)}</ul>;
  if (style === "square") return <ul className="list-none mt-1 space-y-0.5">{items.map((r, i) => <li key={i} className="pl-3 relative before:content-['▪'] before:absolute before:left-0 before:text-gray-500">{r}</li>)}</ul>;
  if (style === "none") return <ul className="list-none mt-1 space-y-0.5">{items.map((r, i) => <li key={i}>{r}</li>)}</ul>;
  return <ul className="list-disc ml-4 mt-1 space-y-0.5">{items.map((r, i) => <li key={i}>{r}</li>)}</ul>;
}

export function TemplateBase({ data, theme: themeIn, overridePrimary }: { data: ResumeData; theme: TemplateTheme; overridePrimary?: string }) {
  // Override only the primary accent; preserve each template's intentional band/sidebar background.
  const pair = getFontPair(data.fontFamily);
  let t: TemplateTheme = overridePrimary ? { ...themeIn, primary: overridePrimary } : themeIn;
  if (pair.bodyFont) t = { ...t, font: pair.bodyFont, headingFont: pair.headingFont || pair.bodyFont };
  const { personalInfo: p } = data;
  const sectionOrder = getOrderedSections(data);

  const headingClass = `text-xs font-bold mb-1.5 pb-1 ${t.uppercaseHeadings !== false ? "uppercase" : ""}`;
  const headingStyle: React.CSSProperties = {
    color: t.primary,
    letterSpacing: t.headingTrack || "0.08em",
    borderBottom: `1px solid ${t.accentRule || "#e2e8f0"}`,
    fontFamily: t.headingFont || t.font,
  };

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="mb-4 last:mb-0">
      <h2 className={headingClass} style={headingStyle}>{title}</h2>
      {children}
    </div>
  );

  const SideSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="mb-4 last:mb-0">
      <h2 className={`text-[10px] font-bold mb-1.5 pb-1 ${t.uppercaseHeadings !== false ? "uppercase" : ""}`} style={{ color: t.sidebarText || t.primary, letterSpacing: t.headingTrack || "0.1em", borderBottom: `1px solid ${t.sidebarText ? "rgba(255,255,255,0.25)" : (t.accentRule || "#cbd5e1")}` }}>{title}</h2>
      {children}
    </div>
  );

  // === Renderers ===
  const renderers: Record<string, React.ReactNode> = {
    summary: data.summary ? <Section key="summary" title="Summary"><p>{data.summary}</p></Section> : null,
    workExperience: data.workExperience.length > 0 ? (
      <Section key="workExperience" title="Experience">
        {data.workExperience.map((exp) => (
          <div key={exp.id} className="mb-3 last:mb-0">
            <div className="flex items-start justify-between gap-3">
              <div><strong>{exp.jobTitle}</strong>{exp.company && <span className="font-medium"> · {exp.company}</span>}</div>
              <span className="text-[10px] whitespace-nowrap" style={{ color: t.muted }}>{exp.startDate && formatDate(exp.startDate)} — {exp.currentlyWorking ? "Present" : exp.endDate ? formatDate(exp.endDate) : ""}</span>
            </div>
            {exp.location && <p className="text-[10px]" style={{ color: t.muted }}>{exp.location}</p>}
            {bulletList(exp.responsibilities.filter(Boolean), t.bullet)}
          </div>
        ))}
      </Section>
    ) : null,
    education: data.education.length > 0 ? (
      <Section key="education" title="Education">
        {data.education.map((edu) => (
          <div key={edu.id} className="mb-2 last:mb-0">
            <div className="flex items-start justify-between gap-3">
              <div><strong>{edu.degree}</strong>{edu.institution && <span> — {edu.institution}</span>}</div>
              {edu.graduationDate && <span className="text-[10px] whitespace-nowrap" style={{ color: t.muted }}>{formatDate(edu.graduationDate)}</span>}
            </div>
            {edu.location && <p className="text-[10px]" style={{ color: t.muted }}>{edu.location}</p>}
            {edu.gpa && <p className="text-[10px]">GPA: {edu.gpa}</p>}
            {edu.honors && <p className="text-[10px]">{edu.honors}</p>}
          </div>
        ))}
      </Section>
    ) : null,
    skills: data.skills.length > 0 ? (
      <Section key="skills" title="Skills">
        {data.skills.map((g) => (
          <div key={g.id} className="mb-1.5 last:mb-0">
            {g.category && <strong style={{ color: t.primary }}>{g.category}: </strong>}
            <span>{g.skills.filter(Boolean).join(" · ")}</span>
          </div>
        ))}
      </Section>
    ) : null,
    projects: data.projects.length > 0 ? (
      <Section key="projects" title="Projects">
        {data.projects.map((proj) => (
          <div key={proj.id} className="mb-2 last:mb-0">
            <strong>{proj.title}</strong>{proj.role && <span> — {proj.role}</span>}
            {proj.technologies.filter(Boolean).length > 0 && <p className="text-[10px]" style={{ color: t.muted }}>{proj.technologies.join(", ")}</p>}
            {proj.description && <p className="mt-0.5">{proj.description}</p>}
          </div>
        ))}
      </Section>
    ) : null,
    certifications: data.certifications.length > 0 ? (
      <Section key="certifications" title="Certifications">
        {data.certifications.map((c) => (
          <div key={c.id} className="mb-1 last:mb-0">
            <strong>{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: t.primary }}>{c.name}</a> : c.name}</strong>{c.issuer && <span> — {c.issuer}</span>}
            {c.issueDate && <span className="text-[10px] ml-2" style={{ color: t.muted }}>{formatDate(c.issueDate)}</span>}
          </div>
        ))}
      </Section>
    ) : null,
    languages: data.languages.length > 0 ? (
      <Section key="languages" title="Languages">
        <p>{data.languages.map((l) => `${l.language} (${l.proficiency})`).join(" · ")}</p>
      </Section>
    ) : null,
    achievements: data.achievements.filter(Boolean).length > 0 ? (
      <Section key="achievements" title="Achievements">{bulletList(data.achievements.filter(Boolean), t.bullet)}</Section>
    ) : null,
  };

  // Sidebar variants share the same renderer set, but with side-styled section headings
  const sideRenderers: Record<string, React.ReactNode> = {
    skills: data.skills.length > 0 ? (
      <SideSection key="skills" title="Skills">
        {data.skills.map((g) => (
          <div key={g.id} className="mb-2 last:mb-0">
            {g.category && <p className="text-[10px] font-semibold mb-0.5" style={{ color: t.sidebarText || t.primary }}>{g.category}</p>}
            <p className="text-[10px]">{g.skills.filter(Boolean).join(", ")}</p>
          </div>
        ))}
      </SideSection>
    ) : null,
    languages: data.languages.length > 0 ? (
      <SideSection key="languages" title="Languages">
        {data.languages.map((l, i) => <p key={i} className="text-[10px]">{l.language} — {l.proficiency}</p>)}
      </SideSection>
    ) : null,
    certifications: data.certifications.length > 0 ? (
      <SideSection key="certifications" title="Certifications">
        {data.certifications.map((c) => (
          <div key={c.id} className="mb-1.5">
            <p className="text-[10px] font-semibold">{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>{c.name}</a> : c.name}</p>
            {c.issuer && <p className="text-[10px]">{c.issuer}</p>}
          </div>
        ))}
      </SideSection>
    ) : null,
    achievements: data.achievements.filter(Boolean).length > 0 ? (
      <SideSection key="achievements" title="Achievements">
        <ul className="list-none space-y-0.5 text-[10px]">{data.achievements.filter(Boolean).map((a, i) => <li key={i}>• {a}</li>)}</ul>
      </SideSection>
    ) : null,
  };

  const baseStyle: React.CSSProperties = {
    fontFamily: t.font,
    color: t.text,
    background: "#ffffff",
    colorScheme: "light",
  };

  // === Layouts ===
  if (t.layout === "sidebar-left" || t.layout === "sidebar-right") {
    const sideKeys = ["skills", "languages", "certifications", "achievements"];
    const mainKeys = sectionOrder.filter((k) => !sideKeys.includes(k));
    const orderedSide = sectionOrder.filter((k) => sideKeys.includes(k));
    const Sidebar = (
      <aside className="w-[36%] p-5 space-y-1" style={{ background: t.sidebarBg || "#f1f5f9", color: t.sidebarText || t.text }}>
        {p.photoUrl && <img src={p.photoUrl} alt="" width={80} height={80} loading="lazy" decoding="async" className="w-20 h-20 rounded-full object-cover mb-3 mx-auto" style={{ border: `2px solid ${t.sidebarText ? "rgba(255,255,255,0.3)" : "#cbd5e1"}` }} />}
        <SideSection title="Contact"><ContactStack p={p} color={t.sidebarText || t.muted} showIcons={t.showIcons !== false} /></SideSection>
        {orderedSide.map((k) => sideRenderers[k])}
      </aside>
    );
    const Main = (
      <main className="flex-1 p-5">
        <header className="mb-4">
          {p.fullName && <h1 className="text-2xl font-bold leading-tight" style={{ color: t.primary, fontFamily: t.headingFont || t.font }}>{p.fullName}</h1>}
          {p.professionalTitle && <p className="text-sm" style={{ color: t.muted }}>{p.professionalTitle}</p>}
        </header>
        {mainKeys.map((k) => renderers[k])}
      </main>
    );
    return (
      <div className="text-xs leading-relaxed flex" style={baseStyle}>
        {t.layout === "sidebar-left" ? <>{Sidebar}{Main}</> : <>{Main}{Sidebar}</>}
      </div>
    );
  }

  if (t.layout === "band") {
    return (
      <div className="text-xs leading-relaxed" style={baseStyle}>
        <header className="px-6 py-5 flex items-center gap-4" style={{ background: t.bandBg || t.primary, color: t.bandText || "#fff" }}>
          {p.photoUrl && <img src={p.photoUrl} alt="" width={64} height={64} loading="lazy" decoding="async" className="w-16 h-16 rounded-full object-cover shrink-0" style={{ border: "2px solid rgba(255,255,255,0.3)" }} />}
          <div className="flex-1">
            {p.fullName && <h1 className="text-2xl font-bold leading-tight" style={{ fontFamily: t.headingFont || t.font }}>{p.fullName}</h1>}
            {p.professionalTitle && <p className="text-sm opacity-90">{p.professionalTitle}</p>}
            <div className="mt-2"><ContactBlock p={p} color={t.bandText || "#fff"} iconColor={t.bandText || "#fff"} showIcons={t.showIcons !== false} /></div>
          </div>
        </header>
        <div className="p-6 space-y-1">{sectionOrder.map((k) => renderers[k])}</div>
      </div>
    );
  }

  if (t.layout === "split-header") {
    return (
      <div className="text-xs leading-relaxed" style={baseStyle}>
        <header className="px-6 pt-6 pb-4 flex items-end justify-between gap-6" style={{ borderBottom: `3px solid ${t.primary}` }}>
          <div>
            {p.fullName && <h1 className="text-3xl font-bold leading-tight" style={{ color: t.primary, fontFamily: t.headingFont || t.font, letterSpacing: t.headingTrack }}>{p.fullName}</h1>}
            {p.professionalTitle && <p className="text-sm mt-0.5" style={{ color: t.muted }}>{p.professionalTitle}</p>}
          </div>
          <ContactStack p={p} color={t.muted} iconColor={t.primary} showIcons={t.showIcons !== false} />
        </header>
        <div className="p-6 space-y-1">{sectionOrder.map((k) => renderers[k])}</div>
      </div>
    );
  }

  // single / single-left
  const align = t.layout === "single-left" ? "text-left" : "text-center";
  const justify = t.layout === "single-left" ? "justify-start" : "justify-center";
  return (
    <div className="text-xs leading-relaxed p-6" style={baseStyle}>
      <header className={`mb-5 ${align}`}>
        {p.photoUrl && <img src={p.photoUrl} alt="" width={80} height={80} loading="lazy" decoding="async" className={`w-20 h-20 rounded-full object-cover mb-3 ${t.layout === "single-left" ? "" : "mx-auto"}`} style={{ border: `2px solid ${t.accentRule || "#cbd5e1"}` }} />}
        {p.fullName && <h1 className="text-2xl font-bold leading-tight" style={{ color: t.primary, fontFamily: t.headingFont || t.font, letterSpacing: t.headingTrack }}>{p.fullName}</h1>}
        {p.professionalTitle && <p className="text-sm" style={{ color: t.muted }}>{p.professionalTitle}</p>}
        <div className={`mt-2 flex ${justify}`}><ContactBlock p={p} color={t.muted} iconColor={t.primary} showIcons={t.showIcons !== false} /></div>
      </header>
      {sectionOrder.map((k) => renderers[k])}
    </div>
  );
}
